<template>
  <d-card
    :id="'global-service-' + serviceData.uuid"
    class="p-3 mb-4">
    <table class="table table-sm table-bordered mb-0">
      <thead class="bg-success-light">
        <tr>
          <th scope="col" class="border-0">
            <small class="font-weight-bold">{{ $t('service.service') }}</small>
          </th>
          <th scope="col" class="border-0" style="width: 100px;">
            <small class="font-weight-bold">{{ $t('ui.piecesAmount') }}</small>
          </th>
          <th
            scope="col" class="border-0" style="width: 160px;">
            <small class="font-weight-bold">{{ $t('price.pricePerItemShort') }}</small>
          </th>
          <th
            scope="col" class="border-0" style="width: 160px;">
            <small class="font-weight-bold">{{ $t('service.setupPrice') }}</small>
          </th>
          <th
            scope="col" class="border-0" style="width: 160px;">
            <small class="font-weight-bold">{{ $t('order.totalCost') }}</small>
          </th>
          <th
            v-if="!readOnly"
            scope="col" class="border-0" style="width: 120px;">
            <small outline class="font-weight-bold">{{ $t('ui.actions') }}</small>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="align-top is-v-select-wrapper p-2">
            <validation-provider
              v-if="!readOnly"
              rules="required"
              :name="'global-service-group-id-' + serviceData.uuid"
              v-slot="{ errors }"
              tag="div"
              :class="{
                'mb-2': false
              }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  class="w-100"
                  :options="servicesGroups"
                  v-model="serviceGroupID"
                  :reduce="group => group.id"
                  :placeholder="$t('service.chooseServiceGroup')"
                  label="name">
                  <template slot="no-options">
                    {{ $t('service.noServiceGroupsMatchingPhrase') }}&hellip;
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center w-100 normal-white-space">
                      {{ option.name }}
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center w-100 normal-white-space">
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>
            </validation-provider>

            <validation-provider
              v-if="!readOnly"
              :hidden="currentServiceGroupHasCalculator"
              rules="required"
              :name="'global-service-id-' + serviceData.uuid"
              v-slot="{ errors }"
              tag="div"
              :class="{
                'is-service-selector-wrapper': true,
                'mt-2': true
              }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  class="w-100"
                  :options="filteredServices"
                  v-model="serviceData.service_id"
                  :reduce="service => service.id"
                  :placeholder="$t('service.chooseService')"
                  label="code"
                  :filterBy="filterByForServices"
                  :disabled="serviceGroupID === null">
                  <template slot="no-options">
                    {{ $t('service.noServicesMatchingPhrase') }}&hellip;
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center w-100 normal-white-space">
                      {{ option.name }}
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center w-100 normal-white-space">
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>
            </validation-provider>

            <div
              v-if="!readOnly && currentServiceGroupHasCalculator"
              class="d-flex mt-2 selected-service-name">
              {{ $t('service.selectedServiceName') }}:
              <span class="text-primary pl-2">{{ selectedServiceName }}</span>
              <small>
                <a
                  v-if="serviceData.service_id"
                  href="#"
                  @click.prevent="resetServiceID"
                  class="text-primary ml-2">
                  <i class="material-icons">edit</i>
                  {{ $t('ui.edit') }}
                </a>
              </small>
            </div>

            <div
              v-if="readOnly"
              class="d-flex mt-2">
              {{ serviceData.name }}
            </div>

            <div class="d-flex mt-2">
              <template v-if="serviceData.files && serviceData.files[0]">
                <div
                  v-for="(file, index) of serviceData.files"
                  :key="'service-file-' + serviceData.uuid + '-' + index">
                  <a
                    href="#"
                    @click.prevent="downloadLogo(file.file_url, file.file_local)">
                    <template v-if="!downloadInProgress">{{ $t('service.downloadLogo') }}</template>
                    <template v-if="downloadInProgress">{{ $t('ui.downloadInProgress') }}&hellip;</template>
                  </a>

                  <a
                    href="#"
                    @click.prevent="removeLogo(file.id, index)"
                    class="text-danger ml-3">
                    <small v-if="!fileRemoveInProgress">&times; {{ $t('ui.delete') }}</small>
                    <small v-if="fileRemoveInProgress">&times; {{ $t('purchase.removeFileInProgress') }}</small>
                  </a>
                </div>
              </template>

              <file-input
                v-if="(!serviceData.files || !serviceData.files.length) && !isViewMode"
                :id="'inputFileVisual' + serviceData.uuid"
                :label="$t('service.selectFileWithLogo')"
                :onChange="setLogo" />
            </div>

            <div
              v-if="isViewMode && serviceData.description_for_customer"
              class="mb-4">
              <strong>{{ $t('service.descriptionFoCustomer') }}:</strong><br>
              <small>{{ serviceData.description_for_customer }}</small>
            </div>

            <div
              v-if="serviceData.sample_images && serviceData.sample_images.length"
              class="border px-3 py-2 mb-3">
              <strong>
                {{ $t('service.sample.samplePhotosTitle') }}:
              </strong>

              <div
                v-for="(sample, index) of serviceData.sample_images"
                :key="'sample-image-' + index + '-' + sample.order_service_id + '-' + sample.id">
                <small>
                  {{ $t('service.sample.samplePhotosDateCreated') }}: {{ sample.created_at }}
                  <span
                    @click.prevent="seeSampleImage(sample.image_preview_url)"
                    class="text-primary"
                    style="cursor: pointer;">
                    <i class="material-icons">visibility</i>
                    {{ $t('service.sample.samplePhotosSeePreview') }}
                  </span>

                  <a
                    :href="sample.image_download_url"
                    download
                    class="ml-2">
                    <i class="material-icons">cloud_download</i>
                    {{ $t('service.sample.samplePhotosDownload') }}
                  </a>
                </small>
              </div>

              <small>
                <a
                  :href="serviceData.sample_images_url"
                  download>
                  <i class="material-icons">cloud_download</i>
                  {{ $t('service.sample.samplePhotosDownloadAll') }}
                </a>
              </small>
            </div>

            <request-creator-service-embroidery-config
              v-if="serviceData.service_id || currentServiceGroupHasCalculator"
              :embroideryItems="embroideryItems"
              :hasBlockedFields="!!(currentServiceGroupHasCalculator && serviceData.service_id)"
              :hasBlockedFieldType="!!currentServiceGroupHasCalculator"
              :readOnly="readOnly"
              :serviceData="serviceData"
              :usesEmbroidery="serviceData.serviceData.uses_embroidery || currentServiceGroupHasCalculator" />

            <div
              v-if="!readOnly && currentServiceGroupHasCalculator && !serviceData.service_id"
              class="mt-2">
              <label>
                <small>
                {{ $t('service.complexity') }}
                </small>
              </label>
              <div class="d-flex image-tooltips">
                <d-button-group
                  size="small">
                  <d-button
                    id="complexity-easy"
                    theme="primary"
                    :outline="complexityLevel !== 'EASY'"
                    @click.prevent="complexityLevel = 'EASY'">
                    EASY
                  </d-button>
                  <d-button
                    id="complexity-medium"
                    theme="primary"
                    :outline="complexityLevel !== 'MEDIUM'"
                    @click.prevent="complexityLevel = 'MEDIUM'">
                    MEDIUM
                  </d-button>
                  <d-button
                    id="complexity-hard"
                    theme="primary"
                    :outline="complexityLevel !== 'HARD'"
                    @click.prevent="complexityLevel = 'HARD'">
                    HARD
                  </d-button>

                  <d-tooltip
                    target="#complexity-easy"
                    container=".image-tooltips"
                    :triggers="['hover']">
                    <img
                      src="@/assets/images/complexity-levels/easy.png"
                      alt="" />
                  </d-tooltip>

                  <d-tooltip
                    target="#complexity-medium"
                    container=".image-tooltips"
                    :triggers="['hover']">
                    <img
                      src="@/assets/images/complexity-levels/medium.png"
                      alt="" />
                  </d-tooltip>

                  <d-tooltip
                    target="#complexity-hard"
                    container=".image-tooltips"
                    :triggers="['hover']">
                    <img
                      src="@/assets/images/complexity-levels/hard.png"
                      alt="" />
                  </d-tooltip>
                </d-button-group>

                <d-button
                  theme="success"
                  class="ml-2"
                  @click.prevent="findServiceByParams()"
                  :disabled="findingServiceInProgress">
                  <div
                    v-if="findingServiceInProgress"
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                  {{ $t('service.findService') }}
                </d-button>
              </div>
            </div>

            <request-creator-service-files
              v-if="isViewMode"
              :serviceFiles="serviceData.files"
              :serviceIdInOrder="serviceData.id"
              :readOnly="true" />
          </td>

          <td class="align-top pt-2">
            <validation-provider
              v-if="!readOnly"
              rules="required|min_value:1"
              :name="'global-service-amount-' + serviceData.uuid"
              v-slot="{ errors }">
              <d-input-group>
                <input
                  :min="1"
                  :invalid="errors.length ? true : null"
                  v-model="serviceData.quantity"
                  :readonly="!serviceData.service_id || readOnly"
                  class="form-control"
                  type="number" />
                <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
              </d-input-group>
            </validation-provider>
            <template v-else>
              {{ serviceData.quantity }} {{ $t('ui.piecesAbbr') }}
            </template>
          </td>

          <td class="align-top pt-2">
            <template v-if="serviceData.service_id">
              <div v-if="whoOrders === 'me'">
                {{ Number(serviceData.price).toFixed(2) }} {{ selectedCurrencySymbol }}
              </div>

              <div v-if="whoOrders === 'my-customer' && !serviceData.use_custom_partner_price">
                {{ Number(serviceData.partner_price).toFixed(2) }} {{ selectedCurrencySymbol }}

                <del
                  v-if="whoOrders === 'my-customer' && serviceData.partner_price !== serviceData.partner_price_before_discount"
                  style="display: block; line-height: 1;">
                  <small>
                    {{ Number(serviceData.partner_price_before_discount).toFixed(2) }} {{ selectedCurrencySymbol }}
                  </small>
                </del>
              </div>

              <div v-if="whoOrders === 'my-customer' && serviceData.use_custom_partner_price">
                <validation-provider
                  rules="required|min_value:0"
                  :name="'service-partner-price-' + serviceData.uuid"
                  v-slot="{ errors }">
                  <d-input-group
                    :append="selectedCurrencySymbol"
                    class="w-100">
                    <input
                      v-model="serviceData.partner_price"
                      :invalid="errors.length ? true : null"
                      :readonly="!serviceData.service_id || readOnly"
                      type="number"
                      step="0.01"
                      class="w-100 text-right form-control"/>
                  </d-input-group>
                </validation-provider>
              </div>

              <a
                v-if="whoOrders === 'my-customer' && !serviceData.use_custom_partner_price && !readOnly"
                href="#"
                class="text-primary"
                @click.prevent="useCustomPartnerPrice">
                <small>
                  <i class="material-icons">edit</i>
                  {{ $t('product.useCustomPartnerPrice') }}
                </small>
              </a>

              <a
                v-if="whoOrders === 'my-customer' && serviceData.use_custom_partner_price && !readOnly"
                href="#"
                class="text-primary"
                @click.prevent="useDefaultPartnerPrice">
                <small>
                  <i class="material-icons">restore</i>
                  {{ $t('product.useDefaultPartnerPrice') }}
                </small>
              </a>

              <small
                v-if="whoOrders === 'my-customer'"
                class="d-block">
                {{ $t('order.includedCustomerDiscount') }}:
                <span>
                  {{ Number(serviceData.partner_price_before_discount - serviceData.partner_price).toFixed(2) }} {{ selectedCurrencySymbol }}<br>
                </span>
                {{ $t('order.includedMarkup') }}:
                <span :class="{ 'text-danger': serviceData.partner_price - serviceData.price < 0 }">
                  {{ Number(serviceData.partner_price - serviceData.price).toFixed(2) }} {{ selectedCurrencySymbol }}<br>
                </span>
                {{ $t('order.yourPrice') }}:
                {{ Number(serviceData.price).toFixed(2) }} {{ selectedCurrencySymbol }}
              </small>

              <del
                v-if="whoOrders === 'me' && serviceData.price !== serviceData.price_before_discount"
                style="display: block; line-height: 1;">
                <small>
                  {{ Number(serviceData.price_before_discount).toFixed(2) }} {{ selectedCurrencySymbol }}
                </small>
              </del>

              <small v-if="serviceData.service_id">
                {{ $t('price.pricePerItemFrom', { price: serviceData.price_multiple_from }) }}
              </small>
            </template>
          </td>
          <td class="align-top pt-2">
            <template v-if="serviceData.service_id">
              <template v-if="whoOrders === 'me'">
                {{ Number(parseFloat(serviceData.price_setup)).toFixed(2) }} {{ selectedCurrencySymbol }}
              </template>

              <div v-if="whoOrders === 'my-customer' && !serviceData.use_custom_setup_partner_price">
                {{ Number(parseFloat(serviceData.partner_price_setup)).toFixed(2) }} {{ selectedCurrencySymbol }}

                <del
                  v-if="whoOrders === 'my-customer' && serviceData.partner_price_setup !== serviceData.partner_price_setup_before_discount"
                  style="display: block; line-height: 1;">
                  <small>
                    {{ Number(serviceData.partner_price_setup_before_discount).toFixed(2) }} {{ selectedCurrencySymbol }}
                  </small>
                </del>
              </div>

              <div v-if="whoOrders === 'my-customer' && serviceData.use_custom_setup_partner_price">
                <validation-provider
                  rules="required|min_value:0"
                  :name="'service-partner-setup-price-' + serviceData.uuid"
                  v-slot="{ errors }">
                  <d-input-group
                    :append="selectedCurrencySymbol"
                    class="w-100">
                    <input
                      v-model="serviceData.partner_price_setup"
                      :invalid="errors.length ? true : null"
                      :readonly="!serviceData.service_id || readOnly"
                      type="number"
                      step="0.01"
                      class="w-100 text-right form-control" />
                  </d-input-group>
                </validation-provider>
              </div>

              <a
                v-if="whoOrders === 'my-customer' && !serviceData.use_custom_setup_partner_price && !readOnly"
                href="#"
                class="text-primary"
                @click.prevent="useCustomSetupPartnerPrice">
                <small>
                  <i class="material-icons">edit</i>
                  {{ $t('product.useCustomPartnerPrice') }}
                </small>
              </a>

              <a
                v-if="whoOrders === 'my-customer' && serviceData.use_custom_setup_partner_price && !readOnly"
                href="#"
                class="text-primary"
                @click.prevent="useDefaultSetupPartnerPrice">
                <small>
                  <i class="material-icons">restore</i>
                  {{ $t('product.useDefaultPartnerPrice') }}
                </small>
              </a>

              <small
                v-if="whoOrders === 'my-customer'"
                class="d-block">
                {{ $t('order.includedCustomerDiscount') }}:
                <span>
                  {{ Number(serviceData.partner_price_setup_before_discount - serviceData.partner_price_setup).toFixed(2) }} {{ selectedCurrencySymbol }}<br>
                </span>
                {{ $t('order.includedMarkup') }}:
                <span :class="{ 'text-danger': serviceData.partner_price_setup - serviceData.price_setup < 0 }">
                  {{ Number(serviceData.partner_price_setup - serviceData.price_setup).toFixed(2) }} {{ selectedCurrencySymbol }}<br>
                </span>
                {{ $t('order.yourPrice') }}:
                {{ Number(serviceData.price_setup).toFixed(2) }} {{ selectedCurrencySymbol }}
              </small>

              <del
                v-if="whoOrders === 'me' && serviceData.price_setup !== serviceData.price_setup_before_discount"
                style="display: block; line-height: 1;">
                <small>
                  {{ Number(serviceData.price_setup_before_discount).toFixed(2) }} {{ selectedCurrencySymbol }}
                </small>
              </del>
            </template>
          </td>
          <td
            class="align-top pt-2">
            <template v-if="serviceData.service_id">
              <template v-if="serviceData.totalPrice && !Number.isNaN(serviceData.totalPrice)">
                <template v-if="whoOrders === 'me'">
                  <template v-if="servicesDiscount > 0">
                    <span class="text-primary" style="display: block; line-height: 1;">{{ serviceData.totalPrice }} {{ selectedCurrencySymbol }}</span>
                    <del
                      v-if="serviceData.totalPriceBeforeDiscount !== serviceData.totalPrice"
                      style="display: block; line-height: 1;"><small>{{ serviceData.totalPriceBeforeDiscount }} {{ selectedCurrencySymbol }}</small></del>
                  </template>
                  <template v-else>
                    <span class="text-primary">
                      {{ serviceData.totalPartnerPrice }} {{ selectedCurrencySymbol }}
                    </span>
                  </template>
                </template>

                <span
                  v-if="whoOrders === 'my-customer'"
                  class="text-primary">
                  {{ serviceData.totalPartnerPrice }} {{ selectedCurrencySymbol }}
                </span>

                <small
                  v-if="whoOrders === 'my-customer'"
                  class="d-block">
                  {{ $t('order.includedMarkup') }}:
                  <span :class="{ 'text-danger': serviceData.totalPartnerPrice - serviceData.totalPrice < 0 }">
                    {{ Number(serviceData.totalPartnerPrice - serviceData.totalPrice).toFixed(2) }} {{ selectedCurrencySymbol }}<br>
                  </span>
                  {{ $t('order.yourPrice') }}:
                  {{ Number(serviceData.totalPrice).toFixed(2) }} {{ selectedCurrencySymbol }}
                </small>
              </template>
            </template>
          </td>
          <td
            v-if="!readOnly"
            class="align-top pt-2">
            <d-button
              size="sm"
              outline
              @click.prevent="duplicateService"
              theme="primary"
              :title="$t('ui.duplicate')"
              class="mr-2">
              <i class="material-icons">content_copy</i>
            </d-button>

            <d-button
              size="sm"
              outline
              @click.prevent="removeService"
              theme="danger"
              :title="$t('ui.delete')">
              <i class="material-icons">delete</i>
            </d-button>
          </td>
        </tr>
      </tbody>
    </table>
  </d-card>
</template>

<script>
import Vue from 'vue';
import FormUtils from './../../utils/FormUtils.js';
import CustomDecimals from './../../utils/CustomDecimals.js';
import RequestCreatorServiceEmbroideryConfig from './RequestCreatorServiceEmbroideryConfig.vue';
import RequestCreatorServiceFiles from './RequestCreatorServiceFiles.vue';

export default {
  name: 'request-creator-global-service',
  props: [
    'currencyId',
    'readOnly',
    'serviceData',
    'selectedCurrencySymbol',
    'services',
    'servicesDiscount',
    'servicesGroups',
    'servicesMarkup',
    'servicesSetupMarkup',
    'partnerServicesDiscount',
    'embroideryItems',
    'whoOrders',
    'useCustomerPricing',
    'orderSetUpInProgress'
  ],
  components: {
    'request-creator-service-embroidery-config': RequestCreatorServiceEmbroideryConfig,
    'request-creator-service-files': RequestCreatorServiceFiles
  },
  computed: {
    isViewMode () {
      return !!this.$route.params.id && this.$route.path.indexOf('/zamowienia/podglad/') > -1;
    },
    filteredServices () {
      if (!this.serviceGroupID) {
        return [];
      }

      return this.services.filter(service => service.group_id === this.serviceGroupID);
    },
    currentServiceGroupHasCalculator () {
      if (!this.servicesGroups) {
        return false;
      }

      let selectedGroup = this.servicesGroups.filter(group => group.id === this.serviceGroupID);

      if (selectedGroup && selectedGroup[0]) {
        return !!selectedGroup[0].has_stitch_calculator;
      }

      return false;
    },
    currentServicePriceMultipleFrom () {
      if (!this.services.length) {
        return null;
      }

      let foundedService = this.services.filter(service => service.id === this.serviceData.service_id);

      if (!(foundedService && foundedService[0])) {
        return null;
      }

      return foundedService[0].price_multiple_from;
    },
    selectedServiceName () {
      if (!this.serviceData.service_id) {
        return '-';
      }

      let selectedService = this.services.filter(service => service.id === this.serviceData.service_id);

      if (selectedService && selectedService[0]) {
        return selectedService[0].name;
      }

      return '-';
    }
  },
  data () {
    return {
      downloadInProgress: false,
      fileRemoveInProgress: false,
      service: {},
      serviceGroupID: null,
      complexityLevel: 'EASY',
      findingServiceInProgress: false
    };
  },
  watch: {
    'serviceGroupID': function (newValue) {
      if (newValue && !this.orderSetUpInProgress) {
        this.$set(this.serviceData, 'service_id', null);
      }

      if (!this.orderSetUpInProgress && this.currentServiceGroupHasCalculator) {
        let selectedGroup = this.servicesGroups.filter(group => group.id === newValue);
        let groupEmbroideryTypeID;

        if (selectedGroup[0]) {
          groupEmbroideryTypeID = selectedGroup[0].embroidery_type_id;
        }

        Vue.nextTick(() => {
          this.$bus.$emit('service-embroidery-config-update', this.serviceData.uuid, groupEmbroideryTypeID);
        });
      }
    },
    'serviceData.service_id': function (newValue) {
      if (newValue && !this.orderSetUpInProgress) {
        this.updateServicePrices();
      }
    },
    'serviceData.quantity': function (newValue) {
      if (newValue !== '' && !this.orderSetUpInProgress) {
        this.updateServicePrices();
      }
    },
    'serviceData.use_custom_partner_price': function () {
      if (!this.orderSetUpInProgress) {
        this.updateServicePrice();
      }
    },
    'serviceData.use_custom_setup_partner_price': function () {
      if (!this.orderSetUpInProgress) {
        this.updateServiceSetupPrice();
      }
    },
    'serviceData.partner_price': function (newValue) {
      if (newValue !== '' && !this.orderSetUpInProgress && this.serviceData.use_custom_partner_price) {
        this.updateServicePrice();
      }
    },
    'serviceData.partner_price_setup': function (newValue) {
      if (newValue !== '' && !this.orderSetUpInProgress && this.serviceData.use_custom_setup_partner_price) {
        this.updateServiceSetupPrice();
      }
    }
  },
  mounted () {
    this.$bus.$on('request-creator-global-services-update-price', this.updateServicePrices);
    this.$bus.$on('request-creator-global-initial-services-update-price', this.initialServicePriceCheck);
    // Set initial group value
    let foundedService = this.services.filter(service => service.id === this.serviceData.service_id);

    if (foundedService && foundedService[0]) {
      this.serviceGroupID = foundedService[0].group_id;
    }
  },
  methods: {
    filterByForServices (option, label, search) {
      return (option.code + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    removeService () {
      this.$bus.$emit('request-creator-remove-global-service', this.serviceData.uuid);
    },
    initialServicePriceCheck () {
      this.updateServicePrice(true);
      this.updateServiceSetupPrice(true);
    },
    updateServicePrices () {
      this.updateServicePrice();
      this.updateServiceSetupPrice();
    },
    updateServiceSetupPrice (initialCheck = false) {
      let serviceID = this.serviceData.service_id;
      let serviceItem;
      let currentSetupPriceBeforeDiscount = parseFloat(this.serviceData.price_setup_before_discount);
      let currentPartnerSetupPriceBeforeDiscount = parseFloat(this.serviceData.partner_price_setup_before_discount);
      let currentPriceMultipleFrom = this.serviceData.price_multiple_from;
      let useCustomSetupPartnerPrice = false;

      if (this.whoOrders === 'my-customer' && this.serviceData.use_custom_setup_partner_price) {
        useCustomSetupPartnerPrice = true;
      }

      if (serviceID) {
        serviceItem = this.services.filter(service => service.id === serviceID)[0];

        this.$bus.$emit('request-creator-update-global-service-fields', {
          service_uuid: this.serviceData.uuid,
          fields: {
            service_id: serviceID,
            serviceData: JSON.parse(JSON.stringify(serviceItem)),
            price_multiple_from: this.currentServicePriceMultipleFrom
          }
        });

        if (initialCheck && currentPriceMultipleFrom !== this.currentServicePriceMultipleFrom) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-multiple-from',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_price_multiple_from: currentPriceMultipleFrom,
            new_price_multiple_from: this.currentServicePriceMultipleFrom
          });
        }
      } else {
        serviceItem = false;
      }

      if (!serviceItem || !serviceItem.prices) {
        let fieldsToUpdate = {
          price_setup: '0.00',
          price_setup_before_discount: '0.00',
          partner_price_setup: '0.00'
        };

        if (useCustomSetupPartnerPrice) {
          fieldsToUpdate = {
            price_setup: '0.00',
            price_setup_before_discount: '0.00'
          };
        }

        if (this.whoOrders === 'my-customer') {
          fieldsToUpdate.partner_price_setup_before_discount = '0.00';

          if (useCustomSetupPartnerPrice) {
            fieldsToUpdate.partner_price_setup_before_discount = this.serviceData.partner_price_setup;
          }
        }

        this.$bus.$emit('request-creator-update-global-service-fields', {
          service_uuid: this.serviceData.uuid,
          fields: fieldsToUpdate
        });

        if (initialCheck && currentSetupPriceBeforeDiscount !== 0) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-setup-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_setup_price: currentSetupPriceBeforeDiscount,
            new_setup_price: 0,
            source: 'prices'
          });
        }

        if (
          initialCheck &&
          this.whoOrders === 'my-customer' &&
          currentPartnerSetupPriceBeforeDiscount !== 0 &&
          !useCustomSetupPartnerPrice
        ) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-setup-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_setup_price: currentPartnerSetupPriceBeforeDiscount,
            new_setup_price: 0,
            source: 'customer_prices'
          });
        }
        return;
      }

      let usePartnerPricing = this.whoOrders === 'my-customer' && this.useCustomerPricing;
      let pricingType = usePartnerPricing ? 'customer_prices' : 'prices';
      let serviceSetupPrices = serviceItem.prices.filter(price => price.currency_id === this.currencyId && price.is_setup);
      let servicePartnerSetupPrices = serviceItem[pricingType].filter(price => price.currency_id === this.currencyId && price.is_setup);

      if (!serviceSetupPrices.length && !servicePartnerSetupPrices.length) {
        let fieldsToUpdate = {
          price_setup: '0.00',
          price_setup_before_discount: '0.00',
          partner_price_setup: '0.00'
        };

        if (useCustomSetupPartnerPrice) {
          fieldsToUpdate = {
            price_setup: '0.00',
            price_setup_before_discount: '0.00'
          };
        }

        if (this.whoOrders === 'my-customer') {
          fieldsToUpdate.partner_price_setup_before_discount = '0.00';

          if (useCustomSetupPartnerPrice) {
            fieldsToUpdate.partner_price_setup_before_discount = this.serviceData.partner_price_setup;
          }
        }

        this.$bus.$emit('request-creator-update-global-service-fields', {
          service_uuid: this.serviceData.uuid,
          fields: fieldsToUpdate
        });

        if (initialCheck && currentSetupPriceBeforeDiscount !== 0) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-setup-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_setup_price: currentSetupPriceBeforeDiscount,
            new_setup_price: 0,
            source: 'prices'
          });
        }

        if (
          initialCheck &&
          currentPartnerSetupPriceBeforeDiscount !== 0 &&
          this.whoOrders === 'my-customer' &&
          !useCustomSetupPartnerPrice
        ) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-setup-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_setup_price: currentPartnerSetupPriceBeforeDiscount,
            new_setup_price: 0,
            source: 'customer_prices'
          });
        }
        return;
      }

      serviceSetupPrices.sort((priceA, priceB) => priceA.value_from - priceB.value_from);
      servicePartnerSetupPrices.sort((priceA, priceB) => priceA.value_from - priceB.value_from);
      let priceIndexToUse = -1;
      let partnerPriceIndexToUse = -1;

      for (let i = 0; i < serviceSetupPrices.length; i++) {
        if (serviceSetupPrices[i].value_from >= this.serviceData.quantity) {
          priceIndexToUse = i;
          break;
        }
      }

      for (let i = 0; i < servicePartnerSetupPrices.length; i++) {
        if (servicePartnerSetupPrices[i].value_from >= this.serviceData.quantity) {
          partnerPriceIndexToUse = i;
          break;
        }
      }

      if (priceIndexToUse === -1 && partnerPriceIndexToUse === -1) {
        let fieldsToUpdate = {
          price_setup: '0.00',
          price_setup_before_discount: '0.00',
          partner_price_setup: '0.00'
        };

        if (useCustomSetupPartnerPrice) {
          fieldsToUpdate = {
            price_setup: '0.00',
            price_setup_before_discount: '0.00'
          };
        }

        if (this.whoOrders === 'my-customer') {
          fieldsToUpdate.partner_price_setup_before_discount = '0.00';

          if (useCustomSetupPartnerPrice) {
            fieldsToUpdate.partner_price_setup_before_discount = this.serviceData.partner_price_setup;
          }
        }

        this.$bus.$emit('request-creator-update-global-service-fields', {
          service_uuid: this.serviceData.uuid,
          fields: fieldsToUpdate
        });

        if (initialCheck && currentSetupPriceBeforeDiscount !== 0) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-setup-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_setup_price: currentSetupPriceBeforeDiscount,
            new_setup_price: 0,
            source: 'prices'
          });
        }

        if (
          initialCheck &&
          currentPartnerSetupPriceBeforeDiscount !== 0 &&
          this.whoOrders === 'my-customer' &&
          !useCustomSetupPartnerPrice
        ) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-setup-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_setup_price: currentPartnerSetupPriceBeforeDiscount,
            new_setup_price: 0,
            source: 'customer_prices'
          });
        }
        return;
      }

      let setupPriceToUse = 0;
      let partnerSetupPriceToUse = 0;

      if (priceIndexToUse > -1) {
        setupPriceToUse = serviceSetupPrices[priceIndexToUse].price;
      }

      if (partnerPriceIndexToUse > -1) {
        partnerSetupPriceToUse = servicePartnerSetupPrices[partnerPriceIndexToUse].price;
      }

      let baseSetupPrice = CustomDecimals.roundProperly(parseFloat(setupPriceToUse) - ((this.servicesDiscount / 100) * parseFloat(setupPriceToUse)));
      let baseSetupPartnerPrice = CustomDecimals.roundProperly(parseFloat(partnerSetupPriceToUse));

      if (pricingType === 'prices') {
        baseSetupPartnerPrice = CustomDecimals.roundProperly(parseFloat(baseSetupPrice));
      }

      let partnerSetupPrice = CustomDecimals.roundProperly(parseFloat(baseSetupPrice) * ((100 + this.servicesSetupMarkup) / 100));

      if (pricingType === 'customer_prices') {
        partnerSetupPrice = CustomDecimals.roundProperly(parseFloat(baseSetupPartnerPrice) * ((100 + this.servicesSetupMarkup) / 100));
      }

      let fieldsToUpdate = {
        price_setup: Number(parseFloat(baseSetupPrice)).toFixed(2),
        price_setup_before_discount: Number(parseFloat(setupPriceToUse)).toFixed(2),
        partner_price_setup: Number(parseFloat(partnerSetupPrice)).toFixed(2)
      };

      if (useCustomSetupPartnerPrice) {
        fieldsToUpdate = {
          price_setup: Number(parseFloat(baseSetupPrice)).toFixed(2),
          price_setup_before_discount: Number(parseFloat(setupPriceToUse)).toFixed(2)
        };
      }

      if (this.whoOrders === 'my-customer') {
        if (useCustomSetupPartnerPrice) {
          fieldsToUpdate.partner_price_setup_before_discount = this.serviceData.partner_price_setup;
        } else if (this.partnerServicesDiscount > 0) {
          fieldsToUpdate.partner_price_setup = CustomDecimals.roundProperly(parseFloat(partnerSetupPrice) - ((this.partnerServicesDiscount / 100) * parseFloat(partnerSetupPrice)));
          fieldsToUpdate.partner_price_setup_before_discount = partnerSetupPrice;
        } else {
          fieldsToUpdate.partner_price_setup_before_discount = fieldsToUpdate.partner_price_setup;
        }
      }

      this.$bus.$emit('request-creator-update-global-service-fields', {
        service_uuid: this.serviceData.uuid,
        fields: fieldsToUpdate
      });

      if (
        initialCheck &&
        currentSetupPriceBeforeDiscount !== parseFloat(Number(parseFloat(setupPriceToUse)).toFixed(2))
      ) {
        this.$bus.$emit('request-creator-initial-price-check', {
          type: 'global-service-setup-price',
          name: this.serviceData.name,
          code: this.serviceData.code,
          old_setup_price: currentSetupPriceBeforeDiscount,
          new_setup_price: parseFloat(Number(parseFloat(setupPriceToUse)).toFixed(2)),
          source: 'prices'
        });
      }

      if (
        initialCheck &&
        !useCustomSetupPartnerPrice &&
        this.whoOrders === 'my-customer' &&
        currentPartnerSetupPriceBeforeDiscount !== parseFloat(Number(parseFloat(partnerSetupPrice)).toFixed(2))
      ) {
        this.$bus.$emit('request-creator-initial-price-check', {
          type: 'global-service-setup-price',
          name: this.serviceData.name,
          code: this.serviceData.code,
          old_setup_price: currentPartnerSetupPriceBeforeDiscount,
          new_setup_price: parseFloat(Number(parseFloat(partnerSetupPrice)).toFixed(2)),
          source: 'customer_prices'
        });
      }
    },
    updateServicePrice (initialCheck = false) {
      let serviceID = this.serviceData.service_id;
      let serviceItem;
      let currentPriceBeforeDiscount = parseFloat(this.serviceData.price_before_discount);
      let currentPartnerPriceBeforeDiscount = parseFloat(this.serviceData.partner_price_before_discount);
      let useCustomPartnerPrice = false;

      if (this.whoOrders === 'my-customer' && this.serviceData.use_custom_partner_price) {
        useCustomPartnerPrice = true;
      }

      if (serviceID) {
        serviceItem = this.services.filter(service => service.id === serviceID)[0];
        this.$bus.$emit('request-creator-update-global-service-fields', {
          service_uuid: this.serviceData.uuid,
          fields: {
            serviceData: JSON.parse(JSON.stringify(serviceItem))
          }
        });
      } else {
        serviceItem = false;
      }

      if (!serviceItem || !serviceItem.prices) {
        let fieldsToUpdate = {
          price: '0.00',
          price_before_discount: '0.00',
          partner_price: '0.00'
        };

        if (useCustomPartnerPrice) {
          fieldsToUpdate = {
            price: '0.00',
            price_before_discount: '0.00'
          };
        }

        if (this.whoOrders === 'my-customer') {
          fieldsToUpdate.partner_price_before_discount = '0.00';

          if (useCustomPartnerPrice) {
            fieldsToUpdate.partner_price_setup_before_discount = this.serviceData.partner_price;
          }
        }

        this.$bus.$emit('request-creator-update-global-service-fields', {
          service_uuid: this.serviceData.uuid,
          fields: fieldsToUpdate
        });

        if (initialCheck && currentPriceBeforeDiscount !== 0) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_price: currentPriceBeforeDiscount,
            new_price: 0,
            source: 'prices'
          });
        }

        if (
          initialCheck &&
          !useCustomPartnerPrice &&
          this.whoOrders === 'my-customer' &&
          currentPartnerPriceBeforeDiscount !== 0
        ) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_price: currentPartnerPriceBeforeDiscount,
            new_price: 0,
            source: 'customer_prices'
          });
        }
        return;
      }

      let usePartnerPricing = this.whoOrders === 'my-customer' && this.useCustomerPricing;
      let pricingType = usePartnerPricing ? 'customer_prices' : 'prices';
      let servicePrices = serviceItem.prices.filter(price => price.currency_id === this.currencyId && !price.is_setup);
      let servicePartnerPrices = serviceItem[pricingType].filter(price => price.currency_id === this.currencyId && !price.is_setup);

      if (!servicePrices.length && !servicePartnerPrices.length) {
        let fieldsToUpdate = {
          price: '0.00',
          price_before_discount: '0.00',
          partner_price: '0.00'
        };

        if (useCustomPartnerPrice) {
          fieldsToUpdate = {
            price: '0.00',
            price_before_discount: '0.00'
          };
        }

        if (this.whoOrders === 'my-customer') {
          fieldsToUpdate.partner_price_before_discount = '0.00';

          if (useCustomPartnerPrice) {
            fieldsToUpdate.partner_price_setup_before_discount = this.serviceData.partner_price;
          }
        }

        this.$bus.$emit('request-creator-update-global-service-fields', {
          service_uuid: this.serviceData.uuid,
          fields: fieldsToUpdate
        });

        if (initialCheck && currentPriceBeforeDiscount !== 0) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_price: currentPriceBeforeDiscount,
            new_price: 0,
            source: 'prices'
          });
        }

        if (
          initialCheck &&
          !useCustomPartnerPrice &&
          this.whoOrders === 'my-customer' &&
          currentPartnerPriceBeforeDiscount !== 0
        ) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_price: currentPartnerPriceBeforeDiscount,
            new_price: 0,
            source: 'customer_prices'
          });
        }
        return;
      }

      servicePrices.sort((priceA, priceB) => priceB.value_from - priceA.value_from);
      servicePartnerPrices.sort((priceA, priceB) => priceB.value_from - priceA.value_from);
      let priceIndexToUse = -1;
      let partnerPriceIndexToUse = -1;

      for (let i = 0; i < servicePrices.length; i++) {
        if (servicePrices[i].value_from <= this.serviceData.quantity) {
          priceIndexToUse = i;
          break;
        }
      }

      for (let i = 0; i < servicePartnerPrices.length; i++) {
        if (servicePartnerPrices[i].value_from <= this.serviceData.quantity) {
          partnerPriceIndexToUse = i;
          break;
        }
      }

      if (priceIndexToUse === -1 && partnerPriceIndexToUse === -1) {
        let fieldsToUpdate = {
          price: '0.00',
          price_before_discount: '0.00',
          partner_price: '0.00'
        };

        if (useCustomPartnerPrice) {
          fieldsToUpdate = {
            price: '0.00',
            price_before_discount: '0.00'
          };
        }

        if (this.whoOrders === 'my-customer') {
          fieldsToUpdate.partner_price_before_discount = '0.00';

          if (useCustomPartnerPrice) {
            fieldsToUpdate.partner_price_setup_before_discount = this.serviceData.partner_price;
          }
        }

        this.$bus.$emit('request-creator-update-global-service-fields', {
          service_uuid: this.serviceData.uuid,
          fields: fieldsToUpdate
        });

        if (initialCheck && currentPriceBeforeDiscount !== 0) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_price: currentPriceBeforeDiscount,
            new_price: 0,
            source: 'prices'
          });
        }

        if (
          initialCheck &&
          !useCustomPartnerPrice &&
          this.whoOrders === 'my-customer' &&
          currentPartnerPriceBeforeDiscount !== 0
        ) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'global-service-price',
            name: this.serviceData.name,
            code: this.serviceData.code,
            old_price: currentPartnerPriceBeforeDiscount,
            new_price: 0,
            source: 'customer_prices'
          });
        }
        return;
      }

      let priceToUse = 0;
      let partnerPriceToUse = 0;

      if (priceIndexToUse > -1) {
        priceToUse = servicePrices[priceIndexToUse].price;
      }

      if (partnerPriceIndexToUse > -1) {
        partnerPriceToUse = servicePartnerPrices[partnerPriceIndexToUse].price;
      }

      let basePrice = CustomDecimals.roundProperly(parseFloat(priceToUse) - ((this.servicesDiscount / 100) * parseFloat(priceToUse)));
      let basePartnerPrice = CustomDecimals.roundProperly(parseFloat(partnerPriceToUse));

      if (pricingType === 'prices') {
        basePartnerPrice = CustomDecimals.roundProperly(parseFloat(basePrice));
      }

      let partnerPrice = CustomDecimals.roundProperly(parseFloat(basePrice) * ((100 + this.servicesMarkup) / 100));

      if (pricingType === 'customer_prices') {
        partnerPrice = CustomDecimals.roundProperly(parseFloat(basePartnerPrice) * ((100 + this.servicesMarkup) / 100));
      }

      let fieldsToUpdate = {
        price: Number(parseFloat(basePrice)).toFixed(2),
        price_before_discount: Number(parseFloat(priceToUse)).toFixed(2),
        partner_price: Number(parseFloat(partnerPrice)).toFixed(2)
      };

      if (useCustomPartnerPrice) {
        fieldsToUpdate = {
          price: Number(parseFloat(basePrice)).toFixed(2),
          price_before_discount: Number(parseFloat(priceToUse)).toFixed(2)
        };
      }

      if (this.whoOrders === 'my-customer') {
        if (useCustomPartnerPrice) {
          fieldsToUpdate.partner_price_before_discount = this.serviceData.partner_price;
        } else if (this.partnerServicesDiscount > 0) {
          fieldsToUpdate.partner_price = CustomDecimals.roundProperly(parseFloat(partnerPrice) - ((this.partnerServicesDiscount / 100) * parseFloat(partnerPrice)));
          fieldsToUpdate.partner_price_before_discount = partnerPrice;
        } else {
          fieldsToUpdate.partner_price_before_discount = fieldsToUpdate.partner_price;
        }
      }

      this.$bus.$emit('request-creator-update-global-service-fields', {
        service_uuid: this.serviceData.uuid,
        fields: fieldsToUpdate
      });

      if (
        initialCheck &&
        currentPriceBeforeDiscount !== parseFloat(Number(parseFloat(priceToUse)).toFixed(2))
      ) {
        this.$bus.$emit('request-creator-initial-price-check', {
          type: 'global-service-price',
          name: this.serviceData.name,
          code: this.serviceData.code,
          old_price: currentPriceBeforeDiscount,
          new_price: parseFloat(Number(parseFloat(priceToUse)).toFixed(2)),
          source: 'prices'
        });
      }

      if (
        initialCheck &&
        !useCustomPartnerPrice &&
        this.whoOrders === 'my-customer' &&
        currentPartnerPriceBeforeDiscount !== parseFloat(Number(parseFloat(partnerPrice)).toFixed(2))
      ) {
        this.$bus.$emit('request-creator-initial-price-check', {
          type: 'global-service-price',
          name: this.serviceData.name,
          code: this.serviceData.code,
          old_price: currentPartnerPriceBeforeDiscount,
          new_price: parseFloat(Number(parseFloat(partnerPrice)).toFixed(2)),
          source: 'customer_prices'
        });
      }
    },
    duplicateService () {
      this.$bus.$emit('request-creator-duplicate-global-service', this.serviceData.uuid);
    },
    setLogo (e) {
      this.$set(this.serviceData, 'fileToUpload', e.target.files[0]);
    },
    removeLogo (fileID, index) {
      if (this.fileRemoveInProgress) {
        return;
      }

      this.fileRemoveInProgress = true;

      this.$http.post('/customer-panel/orders/services/files/delete/' + parseInt(fileID, 10)).then((response) => {
        this.fileRemoveInProgress = false;

        if (response.data && response.data.success) {
          this.serviceData.files.splice(index, 1);
        }
      }).catch(() => {
        this.fileRemoveInProgress = false;
      });
    },
    downloadLogo (url, fileLocal) {
      if (this.downloadInProgress) {
        return;
      }

      this.downloadInProgress = true;

      this.$http.post(url, {}, {
        responseType: 'blob'
      }).then((response) => {
        let link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', fileLocal);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(() => {
        this.downloadInProgress = false;
      });
    },
    useCustomPartnerPrice () {
      Vue.set(this.serviceData, 'use_custom_partner_price', true);
    },
    useDefaultPartnerPrice () {
      Vue.set(this.serviceData, 'use_custom_partner_price', false);
      this.updateServicePrice();
    },
    useCustomSetupPartnerPrice () {
      Vue.set(this.serviceData, 'use_custom_setup_partner_price', true);
    },
    useDefaultSetupPartnerPrice () {
      Vue.set(this.serviceData, 'use_custom_setup_partner_price', false);
      this.updateServiceSetupPrice();
    },
    resetServiceID () {
      this.$set(this.serviceData, 'service_id', null);
    },
    findServiceByParams () {
      if (this.findingServiceInProgress) {
        return;
      }

      if (parseFloat(this.serviceData.embroidery_height) === 0 || parseFloat(this.serviceData.embroidery_width) === 0) {
        Vue.swal({
          title: this.$t('service.wrongDimensions'),
          html: this.$t('service.wrongDimensionsDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      this.findingServiceInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/customer-panel/services/find-by-size ',
        formData: {
          service_group_id: this.serviceGroupID,
          level: this.complexityLevel,
          height: parseFloat(this.serviceData.embroidery_height),
          width: parseFloat(this.serviceData.embroidery_width)
        },
        successAction: (response) => {
          if (!response || !response.data || !response.data.id) {
            Vue.swal({
              title: this.$t('service.serviceNotFound'),
              html: this.$t('service.serviceNotFoundDesc'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.findingServiceInProgress = false;
            return;
          }

          this.$set(this.serviceData, 'service_id', response.data.id);
          this.findingServiceInProgress = false;
        },
        failAction: () => {
          this.findingServiceInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('request-creator-global-services-update-price', this.updateServicePrices);
    this.$bus.$off('request-creator-global-initial-services-update-price', this.initialServicePriceCheck);
  }
}
</script>

<style lang="scss" scoped>
.is-v-select-wrapper > div {
  width: 100%;
}

.cursor-pointer {
  cursor: pointer;
}

.normal-white-space {
  line-height: 1.2;
  white-space: normal;
}

.is-service-selector-wrapper:has(.is-invalid) + .selected-service-name {
  background: rgba(196, 24, 60, .15);
}
</style>
